import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  FlagIcon,
  ChartBarIcon,
  TrophyIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import StandardNavbar from './StandardNavbar';
import srsService from '../services/srsService';

const MockTestSystem = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [testState, setTestState] = useState('setup'); // 'setup', 'running', 'completed'
  const [selectedTest, setSelectedTest] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [testResults, setTestResults] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [mode, setMode] = useState('exam'); // 'exam' | 'practice'
  const [shuffle, setShuffle] = useState(true);
  const [mixXI, setMixXI] = useState(20); // percent XI
  const [pyqPercent, setPyqPercent] = useState(0);
  const [hideTimer, setHideTimer] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [questionStartTs, setQuestionStartTs] = useState(Date.now());
  const [questionTimes, setQuestionTimes] = useState({}); // { [questionId]: ms }
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [incorrectIds, setIncorrectIds] = useState([]);
  const [addWrongToSrs, setAddWrongToSrs] = useState(true);
  const [srsQueued, setSrsQueued] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customCounts, setCustomCounts] = useState({ math: 50, physics: 50, chemistry: 50 });
  const [customDuration, setCustomDuration] = useState(180);

  // Mock test configurations
  const mockTests = [
    {
      id: 'full-mock-1',
      title: 'Full Mock Test 1',
      description: 'Complete 3-hour mock test with 150 questions',
      duration: 180, // minutes
      totalQuestions: 150,
      subjects: ['Mathematics', 'Physics', 'Chemistry'],
      difficulty: 'Mixed',
      price: 0, // Free for now
      isPremium: false
    },
    {
      id: 'subject-wise-maths',
      title: 'Mathematics Mock Test',
      description: '50 questions focused on Mathematics',
      duration: 60,
      totalQuestions: 50,
      subjects: ['Mathematics'],
      difficulty: 'Mixed',
      price: 0,
      isPremium: false
    },
    {
      id: 'subject-wise-physics',
      title: 'Physics Mock Test',
      description: '50 questions focused on Physics',
      duration: 60,
      totalQuestions: 50,
      subjects: ['Physics'],
      difficulty: 'Mixed',
      price: 0,
      isPremium: false
    },
    {
      id: 'subject-wise-chemistry',
      title: 'Chemistry Mock Test',
      description: '50 questions focused on Chemistry',
      duration: 60,
      totalQuestions: 50,
      subjects: ['Chemistry'],
      difficulty: 'Mixed',
      price: 0,
      isPremium: false
    }
  ];

  // Question set for mini mock (fallback seeded; replaced on fetch)
  const [mockQuestions, setMockQuestions] = useState([
    {
      id: 1,
      question: "The value of $\\int_{0}^{\\pi} x \\sin^{3} x dx$ is",
      options: {
        a: "$\\frac{4\\pi}{3}$",
        b: "$\\frac{2\\pi}{3}$",
        c: "0",
        d: "None of these"
      },
      correctAnswer: 'c',
      subject: 'Mathematics',
      difficulty: 'medium',
      explanation: "Using integration by parts and trigonometric identities, the integral evaluates to 0.",
      timeEstimate: 120
    },
    {
      id: 2,
      question: "A particle moves in a straight line with velocity $v = 2t + 3$ m/s. The displacement of the particle in the first 5 seconds is",
      options: {
        a: "25 m",
        b: "30 m",
        c: "35 m",
        d: "40 m"
      },
      correctAnswer: 'd',
      subject: 'Physics',
      difficulty: 'easy',
      explanation: "Displacement = ∫v dt = ∫(2t + 3) dt = t² + 3t. At t=5, displacement = 25 + 15 = 40 m.",
      timeEstimate: 90
    },
    {
      id: 3,
      question: "The IUPAC name of $CH_3-CH_2-CH=CH-CH_3$ is",
      options: {
        a: "2-pentene",
        b: "3-pentene",
        c: "1-pentene",
        d: "4-pentene"
      },
      correctAnswer: 'a',
      subject: 'Chemistry',
      difficulty: 'easy',
      explanation: "The double bond is at position 2, so it's 2-pentene.",
      timeEstimate: 60
    }
  ]);

  // Fetch 30 questions for selected mock test
  const fetchMockQuestions = async (test) => {
    try {
      const subjects = test.subjects || [];
      let pool = [];
      const ref = collection(db, 'mhcet_question_bank_cleaned');
      for (const s of subjects) {
        const q = query(ref, where('subject', '==', s.toLowerCase()))
        const snap = await getDocs(q);
        pool.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      if (pool.length < 10) {
        const ref2 = collection(db, 'questions');
        const snap2 = await getDocs(ref2);
        pool.push(...snap2.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      const transformed = pool.map((q, index) => {
        const text = q.questionText || q.question || `Question ${index + 1}`;
        let opts = {};
        if (Array.isArray(q.options)) {
          ['a','b','c','d'].forEach((k, i) => { if (q.options[i]) opts[k] = q.options[i].text || q.options[i]; });
        } else if (q.options && typeof q.options === 'object') {
          opts = Object.fromEntries(Object.entries(q.options).map(([k, v]) => [k.toLowerCase(), typeof v === 'string' ? v : (v?.text || '')]));
        } else if (q.optionA || q.A) {
          ['a','b','c','d'].forEach(k => { const key = `option${k.toUpperCase()}`; if (q[key] || q[k.toUpperCase()]) opts[k] = q[key] || q[k.toUpperCase()]; });
        }
        const correct = (q.correctAnswer || q.correct_option || q.correctOption || q.answer || 'a').toString().toLowerCase();
        return { id: q.id || index + 1, question: text, options: opts, correctAnswer: correct, subject: (q.subject||'').toString() };
      }).filter(t => Object.keys(t.options).length >= 2);

      let selected = [];
      if (test.id === 'custom') {
        const math = transformed.filter(t => t.subject.toLowerCase().includes('math'));
        const phy = transformed.filter(t => t.subject.toLowerCase().includes('physics'));
        const chem = transformed.filter(t => t.subject.toLowerCase().includes('chem'));
        selected = [
          ...math.sort(()=>Math.random()-0.5).slice(0, customCounts.math||0),
          ...phy.sort(()=>Math.random()-0.5).slice(0, customCounts.physics||0),
          ...chem.sort(()=>Math.random()-0.5).slice(0, customCounts.chemistry||0)
        ];
      } else {
        selected = transformed.sort(() => Math.random() - 0.5).slice(0, test.totalQuestions || 30);
      }
      if (selected.length > 0) setMockQuestions(selected);
    } catch (e) {
      console.warn('Mock fetch fallback', e);
    }
  };

  useEffect(() => {
    let interval;
    if (testState === 'running' && timeLeft > 0 && !isPaused) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTestComplete();
            return 0;
          }
          const next = prev - 1;
          try {
            localStorage.setItem('mock_resume', JSON.stringify({
              testId: selectedTest?.id,
              title: selectedTest?.title,
              currentQuestionIndex,
              answers,
              flagged: Array.from(flaggedQuestions || []),
              timeLeft: next,
              mode,
              shuffle,
              mixXI,
              pyqPercent
            }));
          } catch {}
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [testState, timeLeft, isPaused, selectedTest, currentQuestionIndex, answers, flaggedQuestions, mode, shuffle, mixXI, pyqPercent]);

  // Global handlers for keyboard shortcuts
  const saveAndNextGlobal = () => {
    if (testState !== 'running') return;
    const currentQuestion = mockQuestions[currentQuestionIndex];
    if (!currentQuestion) return;
    const now = Date.now();
    const qid = currentQuestion.id;
    const spent = now - questionStartTs;
    setQuestionTimes(prev => {
      const next = { ...prev, [qid]: (prev[qid] || 0) + spent };
      try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), questionTimes: next })); } catch {}
      return next;
    });
    setQuestionStartTs(now);
    setCurrentQuestionIndex(i => Math.min((selectedTest?.totalQuestions || mockQuestions.length) - 1, i + 1));
  };

  const markAndNextGlobal = () => {
    if (testState !== 'running') return;
    const currentQuestion = mockQuestions[currentQuestionIndex];
    if (!currentQuestion) return;
    handleFlagQuestion(currentQuestion.id);
    saveAndNextGlobal();
  };

  const clearResponseGlobal = () => {
    if (testState !== 'running') return;
    const currentQuestion = mockQuestions[currentQuestionIndex];
    if (!currentQuestion) return;
    setAnswers(prev => {
      const next = { ...prev };
      delete next[currentQuestion.id];
      try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), answers: next })); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (testState !== 'running') return;
      const currentQuestion = mockQuestions[currentQuestionIndex];
      if (!currentQuestion) return;
      if (e.key >= '1' && e.key <= '4') {
        const keys = ['a','b','c','d'];
        const optKey = keys[parseInt(e.key,10)-1];
        if (optKey) handleAnswerSelect(currentQuestion.id, optKey);
      } else if (e.key.toLowerCase() === 's') {
        saveAndNextGlobal();
      } else if (e.key.toLowerCase() === 'm') {
        markAndNextGlobal();
      } else if (e.key.toLowerCase() === 'c') {
        clearResponseGlobal();
      } else if (e.key === 'ArrowLeft') {
        setCurrentQuestionIndex(i => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentQuestionIndex(i => Math.min((selectedTest?.totalQuestions || mockQuestions.length) - 1, i + 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [testState, currentQuestionIndex, mockQuestions, questionStartTs, selectedTest]);

  // Ensure questions are loaded when resuming
  useEffect(() => {
    if (testState === 'running' && selectedTest && mockQuestions.length === 0) {
      fetchMockQuestions(selectedTest);
    }
  }, [testState, selectedTest, mockQuestions.length]);

  useEffect(() => {
    // hydrate resume on load
    if (testState !== 'setup') return;
    try {
      const saved = JSON.parse(localStorage.getItem('mock_resume') || 'null');
      if (saved && mockTests.find(t => t.id === saved.testId)) {
        setSelectedTest(mockTests.find(t => t.id === saved.testId));
        // show resume pill via state kept in setup screen
      }
    } catch {}
  }, [testState]);

  const openSetupFor = (test) => {
    setSelectedTest(test);
    setShowSetupModal(true);
    setMode('exam');
    setShuffle(true);
    setMixXI(20);
    setPyqPercent(0);
  };

  const confirmStart = async () => {
    if (!selectedTest) return;
    setTimeLeft((selectedTest.duration || 60) * 60);
    setTestState('running');
    setAnswers({});
    setCurrentQuestionIndex(0);
    setFlaggedQuestions(new Set());
    setIsPaused(false);
    setHideTimer(false);
    setQuestionStartTs(Date.now());
    setQuestionTimes({});
    toast.success(`Starting ${selectedTest.title}! Good luck! 🚀`);
    await fetchMockQuestions(selectedTest);
    setShowSetupModal(false);
    try {
      const doc = await addDoc(collection(db, 'mockAttempts'), {
        userId: user?.uid || 'anon',
        testId: selectedTest.id,
        title: selectedTest.title,
        startedAt: new Date(),
        mode,
        shuffle,
        mixXI,
        pyqPercent
      });
      setAttemptId(doc.id);
    } catch {}
    try { localStorage.setItem('mock_resume', JSON.stringify({ testId: selectedTest.id, title: selectedTest.title, currentQuestionIndex: 0, answers: {}, flagged: [], timeLeft: (selectedTest.duration||60)*60, mode, shuffle, mixXI, pyqPercent })); } catch {}
  };

  const handleTestComplete = () => {
    // clear resume
    try { localStorage.removeItem('mock_resume'); } catch {}
    const correctAnswers = Object.keys(answers).filter(qId => {
      const question = mockQuestions.find(q => String(q.id) === String(qId));
      return question && String(answers[qId]).toLowerCase() === String(question.correctAnswer).toLowerCase();
    }).length;

    const totalAnswered = Object.keys(answers).length;
    const accuracy = totalAnswered > 0 ? (correctAnswers / totalAnswered) * 100 : 0;
    const timeTaken = selectedTest.duration * 60 - timeLeft;

    // PCM scoring and subject stats
    let pcmScore = 0;
    let subjectStats = {
      Physics: { score: 0, max: 50, answered: 0, correct: 0, total: 50, timeMs: 0 },
      Chemistry: { score: 0, max: 50, answered: 0, correct: 0, total: 50, timeMs: 0 },
      Mathematics: { score: 0, max: 100, answered: 0, correct: 0, total: 50, timeMs: 0 }
    };
    const incorrect = [];
    const done = [];
    mockQuestions.forEach(q => {
      const sel = answers[q.id];
      const isCorrect = sel && String(sel).toLowerCase() === String(q.correctAnswer).toLowerCase();
      const subj = (q.subject || '').toString();
      const t = questionTimes[q.id] || 0;
      if (sel) done.push(q.id);
      if (!isCorrect) incorrect.push(q.id);
      if (subj.toLowerCase().includes('physics')) {
        subjectStats.Physics.answered += sel ? 1 : 0;
        subjectStats.Physics.correct += isCorrect ? 1 : 0;
        subjectStats.Physics.score += isCorrect ? 1 : 0;
        subjectStats.Physics.timeMs += t;
      } else if (subj.toLowerCase().includes('chem')) {
        subjectStats.Chemistry.answered += sel ? 1 : 0;
        subjectStats.Chemistry.correct += isCorrect ? 1 : 0;
        subjectStats.Chemistry.score += isCorrect ? 1 : 0;
        subjectStats.Chemistry.timeMs += t;
      } else if (subj.toLowerCase().includes('math')) {
        subjectStats.Mathematics.answered += sel ? 1 : 0;
        subjectStats.Mathematics.correct += isCorrect ? 1 : 0;
        subjectStats.Mathematics.score += isCorrect ? 2 : 0;
        subjectStats.Mathematics.timeMs += t;
      }
    });
    setCompletedQuestions(done);
    setIncorrectIds(incorrect);
    pcmScore = subjectStats.Physics.score + subjectStats.Chemistry.score + subjectStats.Mathematics.score;

    const results = {
      testId: selectedTest.id,
      testTitle: selectedTest.title,
      totalQuestions: selectedTest.totalQuestions,
      answeredQuestions: totalAnswered,
      correctAnswers,
      accuracy: Math.round(accuracy * 100) / 100,
      timeTaken: Math.round(timeTaken / 60 * 100) / 100,
      timeLeft: Math.round(timeLeft / 60 * 100) / 100,
      flaggedQuestions: flaggedQuestions.size,
      subjectBreakdown: subjectStats,
      pcmScore
    };

    // Persist simple stats for catalog band (localStorage per-user)
    try {
      const uid = user?.uid || 'anon';
      const key = `mock_stats_${uid}_${selectedTest.id}`;
      const prev = JSON.parse(localStorage.getItem(key) || 'null');
      const best = Math.max(prev?.bestScore || 0, pcmScore || 0);
      const stat = { lastScore: pcmScore, bestScore: best, lastAttemptAt: new Date().toISOString() };
      localStorage.setItem(key, JSON.stringify(stat));
    } catch {}

    // Log attempt result
    try {
      addDoc(collection(db, 'mockAttemptResults'), {
        attemptId: attemptId || null,
        userId: user?.uid || 'anon',
        testId: selectedTest.id,
        title: selectedTest.title,
        pcmScore,
        accuracy: results.accuracy,
        completedAt: new Date(),
        totalQuestions: selectedTest.totalQuestions
      });
    } catch {}

    setTestResults(results);
    setTestState('completed');
    toast.success('Test completed! Check your results below.');

    // Queue wrong answers to SRS (best-effort)
    if (addWrongToSrs && user?.uid && incorrect.length) {
      try {
        incorrect.forEach(id => {
          const q = mockQuestions.find(qq => qq.id === id) || {};
          const conceptId = q.subskill || q.topic || (q.subject ? `${q.subject.toLowerCase()}_generic` : 'pcm_generic');
          const review = srsService.sm2plus({ correct: false, latencyMs: 5000, difficulty: q.difficulty || 'medium' });
          srsService.updateMastery(user.uid, conceptId, review, 5000, false);
        });
        setSrsQueued(true);
      } catch {}
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => {
      const next = { ...prev, [questionId]: answer };
      try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), answers: next })); } catch {}
      return next;
    });
  };

  const handleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), flagged: Array.from(newSet) })); } catch {}
      return newSet;
    });
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderWithLatex = (text) => {
    if (!text) return '';
    
    const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/g);
    return parts.map((part, index) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        return <BlockMath key={index} math={part.slice(2, -2)} />;
      } else if (part.startsWith('$') && part.endsWith('$')) {
        return <InlineMath key={index} math={part.slice(1, -1)} />;
      }
      return part;
    });
  };

  // Test Setup Screen
  if (testState === 'setup') {
    return (
      <div className="min-h-screen bg-gray-50">
        <StandardNavbar title="Mock Tests" showBack={true} showHome={true} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Mock Tests</h1>
            <p className="text-gray-600">
              Practice with full-length mock tests designed to simulate the actual MH-CET exam experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockTests.map((test) => (
              <div key={test.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{test.title}</h3>
                  {test.isPremium && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                      Premium
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 mb-4">{test.description}</p>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    {test.duration} minutes
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <AcademicCapIcon className="h-4 w-4 mr-2" />
                    {test.totalQuestions} questions
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <ChartBarIcon className="h-4 w-4 mr-2" />
                    {test.subjects.join(', ')}
                  </div>
                </div>
                {(() => { try { const saved = JSON.parse(localStorage.getItem('mock_resume')||'null'); if (saved && saved.testId === test.id && saved.timeLeft>0) { return (
                  <div className="mb-3 flex items-center justify-between text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-2">
                    <span>Resume: Q{(saved.currentQuestionIndex||0)+1} • {Math.floor((saved.timeLeft||0)/60)}m left</span>
                    <button onClick={() => { setSelectedTest(test); setMode(saved.mode||'exam'); setShuffle(!!saved.shuffle); setMixXI(saved.mixXI||20); setPyqPercent(saved.pyqPercent||0); setAnswers(saved.answers||{}); setFlaggedQuestions(new Set(saved.flagged||[])); setCurrentQuestionIndex(saved.currentQuestionIndex||0); setTimeLeft(saved.timeLeft||0); setShowSetupModal(false); setTestState('running'); }} className="px-2 py-1 bg-blue-600 text-white rounded">Resume</button>
                  </div>
                ); } } catch {} return null; })()}
                {(() => { try { const uid = user?.uid || 'anon'; const st = JSON.parse(localStorage.getItem(`mock_stats_${uid}_${test.id}`)||'null'); if (st) { const d = new Date(st.lastAttemptAt); return (
                  <div className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-2">
                    Last: <span className="font-medium">{st.lastScore}/200</span> • Best: <span className="font-medium">{st.bestScore}/200</span> • {isNaN(d) ? '' : `Last attempt: ${d.toLocaleDateString()}`}
                  </div>
                ); } } catch {} return null; })()}

                <button
                  onClick={() => openSetupFor(test)}
                  className="mt-auto w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Test
                </button>
              </div>
            ))}
            {/* Custom Mock Card */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-gray-900">Custom Mock</h3>
              </div>
              <p className="text-gray-600 mb-4">Choose questions per subject, XI/XII mix, PYQ%, and duration.</p>
              <button onClick={() => { setSelectedTest({ id: 'custom', title: 'Custom Mock', duration: 60, totalQuestions: 150, subjects: ['Mathematics','Physics','Chemistry'] }); setShowCustom(true); }} className="mt-auto w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">Create your own</button>
            </div>
          </div>
        </div>

        {/* Setup Modal */}
        {showSetupModal && selectedTest && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Configure Test</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Mode</label>
                  <div className="flex gap-2">
                    <button onClick={() => setMode('exam')} className={`px-3 py-2 rounded-md border ${mode==='exam'?'border-blue-600 bg-blue-50 text-blue-700':'hover:bg-gray-50'}`}>Exam</button>
                    <button onClick={() => setMode('practice')} className={`px-3 py-2 rounded-md border ${mode==='practice'?'border-blue-600 bg-blue-50 text-blue-700':'hover:bg-gray-50'}`}>Practice</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Timer</label>
                  <div className="px-3 py-2 rounded-md border bg-gray-50">{selectedTest.duration} min</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Shuffle questions</label>
                  <button onClick={() => setShuffle(v=>!v)} className={`px-3 py-2 rounded-md border ${shuffle?'border-blue-600 bg-blue-50 text-blue-700':'hover:bg-gray-50'}`}>{shuffle ? 'On' : 'Off'}</button>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">XI / XII mix</label>
                  <input type="range" min="0" max="100" value={mixXI} onChange={(e)=>setMixXI(parseInt(e.target.value,10))} className="w-full" />
                  <div className="text-xs text-gray-600 mt-1">XI {mixXI}% / XII {100-mixXI}%</div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">PYQ percentage</label>
                  <input type="range" min="0" max="100" value={pyqPercent} onChange={(e)=>setPyqPercent(parseInt(e.target.value,10))} className="w-full" />
                  <div className="text-xs text-gray-600 mt-1">{pyqPercent}% PYQ</div>
                </div>
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={()=>setShowSetupModal(false)} className="px-4 py-2 rounded-md border">Cancel</button>
                <button onClick={confirmStart} className="px-4 py-2 rounded-md bg-blue-600 text-white">Start Test</button>
              </div>
              <div className="mt-4 text-xs text-gray-600">
                PCM scoring: P/C = 1 mark each; Math = 2 marks; Total = 200; No negative. Interface mimics CET; calculator not allowed.
              </div>
            </div>
          </div>
        )}

        {/* Custom Builder Modal */}
        {showCustom && selectedTest && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Custom Mock</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Math questions</label>
                  <input type="number" min="0" max="150" value={customCounts.math} onChange={(e)=>setCustomCounts({...customCounts, math: parseInt(e.target.value||'0',10)})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Physics questions</label>
                  <input type="number" min="0" max="150" value={customCounts.physics} onChange={(e)=>setCustomCounts({...customCounts, physics: parseInt(e.target.value||'0',10)})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Chemistry questions</label>
                  <input type="number" min="0" max="150" value={customCounts.chemistry} onChange={(e)=>setCustomCounts({...customCounts, chemistry: parseInt(e.target.value||'0',10)})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Duration (min)</label>
                  <input type="number" min="10" max="240" value={customDuration} onChange={(e)=>setCustomDuration(parseInt(e.target.value||'60',10))} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">XI / XII mix</label>
                  <input type="range" min="0" max="100" value={mixXI} onChange={(e)=>setMixXI(parseInt(e.target.value,10))} className="w-full" />
                  <div className="text-xs text-gray-600 mt-1">XI {mixXI}% / XII {100-mixXI}%</div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">PYQ percentage</label>
                  <input type="range" min="0" max="100" value={pyqPercent} onChange={(e)=>setPyqPercent(parseInt(e.target.value,10))} className="w-full" />
                  <div className="text-xs text-gray-600 mt-1">{pyqPercent}% PYQ</div>
                </div>
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={()=>setShowCustom(false)} className="px-4 py-2 rounded-md border">Cancel</button>
                <button onClick={() => { const tot = (customCounts.math||0)+(customCounts.physics||0)+(customCounts.chemistry||0); setSelectedTest({ ...selectedTest, totalQuestions: tot, duration: customDuration }); setShowCustom(false); setShowSetupModal(true); }} className="px-4 py-2 rounded-md bg-blue-600 text-white">Continue</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Test Running Screen
  if (testState === 'running') {
    const currentQuestion = mockQuestions[currentQuestionIndex];
    const totalQuestions = selectedTest.totalQuestions;
    const answeredCount = Object.keys(answers).length;

    if (!currentQuestion) {
      return (
        <div className="min-h-screen bg-gray-50">
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{selectedTest?.title || 'Mock Test'}</h1>
                <p className="text-sm text-gray-600">Loading questions…</p>
              </div>
              <button onClick={() => setTestState('setup')} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Back</button>
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">Preparing your test…</div>
          </div>
        </div>
      );
    }

    const saveAndNext = () => {
      const now = Date.now();
      const qid = currentQuestion.id;
      const spent = now - questionStartTs;
      setQuestionTimes(prev => {
        const next = { ...prev, [qid]: (prev[qid] || 0) + spent };
        try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), questionTimes: next })); } catch {}
        return next;
      });
      setQuestionStartTs(now);
      setCurrentQuestionIndex(Math.min(totalQuestions - 1, currentQuestionIndex + 1));
    };
    const markAndNext = () => {
      handleFlagQuestion(currentQuestion.id);
      saveAndNext();
    };
    const clearResponse = () => {
      setAnswers(prev => {
        const next = { ...prev };
        delete next[currentQuestion.id];
        try { localStorage.setItem('mock_resume', JSON.stringify({ ...(JSON.parse(localStorage.getItem('mock_resume')||'{}')), answers: next })); } catch {}
        return next;
      });
    };
    const handleExit = () => {
      try {
        localStorage.setItem('mock_resume', JSON.stringify({
          testId: selectedTest?.id,
          title: selectedTest?.title,
          currentQuestionIndex,
          answers,
          flagged: Array.from(flaggedQuestions || []),
          timeLeft,
          mode,
          shuffle,
          mixXI,
          pyqPercent,
          questionTimes
        }));
      } catch {}
      setTestState('setup');
    };

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{selectedTest.title}</h1>
                <p className="text-sm text-gray-600">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <button onClick={handleExit} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Back</button>
                <div className={`flex items-center ${hideTimer ? 'invisible' : 'text-red-600'}`}>
                  <ClockIcon className="h-5 w-5 mr-2" />
                  <span className="font-mono text-lg">
                    {formatTime(timeLeft)}
                  </span>
                </div>
                <button
                  onClick={() => setHideTimer(v=>!v)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  {hideTimer ? 'Show Timer' : 'Hide Timer'}
                </button>
                {mode === 'practice' && (
                  <button
                    onClick={() => setIsPaused(!isPaused)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Question Navigation */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Question Navigation</h3>
              <div className="text-sm text-gray-600">
                {answeredCount} of {totalQuestions} answered
              </div>
            </div>
            <div className="flex items-center gap-3 mb-4 text-xs">
              <span className="px-2 py-1 rounded bg-gray-100">Not visited</span>
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-900">Not answered</span>
              <span className="px-2 py-1 rounded bg-green-100 text-green-900">Answered</span>
              <span className="px-2 py-1 rounded border border-purple-400 text-purple-700">Marked</span>
            </div>
            <div className="grid grid-cols-10 gap-2 mb-3">
              {Array.from({ length: totalQuestions }, (_, i) => {
                const isAnswered = answers[i + 1];
                const isFlagged = flaggedQuestions.has(i + 1);
                const isCurrent = i === currentQuestionIndex;
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentQuestionIndex(i)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium border-2 transition-colors ${isCurrent ? 'bg-blue-600 text-white border-blue-600' : ''} ${isAnswered && !isCurrent ? 'bg-green-100 text-green-800 border-green-300' : ''} ${!isAnswered && !isCurrent ? 'bg-gray-100 text-gray-600 border-gray-300' : ''} ${isFlagged ? 'ring-2 ring-purple-400' : ''}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={()=>{}} className="px-2 py-1 border rounded">All</button>
              <button onClick={()=>{}} className="px-2 py-1 border rounded">Not answered</button>
              <button onClick={()=>{}} className="px-2 py-1 border rounded">Marked</button>
            </div>
          </div>

          {/* Current Question */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-lg font-semibold text-gray-900">
                  Question {currentQuestionIndex + 1}
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {currentQuestion?.subject}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                  {currentQuestion?.difficulty}
                </span>
              </div>
              
              <button
                onClick={() => handleFlagQuestion(currentQuestion.id)}
                className={`p-2 rounded-lg ${
                  flaggedQuestions.has(currentQuestion.id)
                    ? 'bg-yellow-100 text-yellow-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FlagIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-8">
              <div className="text-lg text-gray-900 mb-6">
                {renderWithLatex(currentQuestion?.question)}
              </div>

              <div className="space-y-4">
                {Object.entries(currentQuestion?.options || {}).map(([key, value]) => (
                  <label
                    key={key}
                    className={`
                      flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors
                      ${answers[currentQuestion?.id] === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion?.id || 'q'}`}
                      value={key}
                      checked={answers[currentQuestion?.id] === key}
                      onChange={() => handleAnswerSelect(currentQuestion?.id, key)}
                      className="sr-only"
                    />
                    <div className="flex items-center w-full">
                      <div className="w-6 h-6 border-2 rounded-full mr-4 flex items-center justify-center">
                        {answers[currentQuestion?.id] === key && (
                          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        )}
                      </div>
                      <span className="font-medium text-gray-700 mr-2">({key.toUpperCase()})</span>
                      <span className="text-gray-900">{renderWithLatex(value)}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                disabled={currentQuestionIndex === 0}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Previous
              </button>
              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={clearResponse} className="px-4 py-2 border rounded-lg">Clear Response</button>
                <button onClick={markAndNext} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center"><FlagIcon className="h-4 w-4 mr-1"/> Mark & Next</button>
                <button onClick={saveAndNext} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">Save & Next <ArrowRightIcon className="h-4 w-4 ml-2" /></button>
                <button onClick={() => setShowSubmitConfirm(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Submit</button>
              </div>
            </div>
            {showSubmitConfirm && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">Submit test?</h4>
                  <p className="text-sm text-gray-700 mb-4">Unanswered: {totalQuestions - answeredCount} • Marked: {flaggedQuestions.size}</p>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowSubmitConfirm(false)} className="px-4 py-2 border rounded-md">Cancel</button>
                    <button onClick={handleTestComplete} className="px-4 py-2 bg-green-600 text-white rounded-md">Submit</button>
                  </div>
                </div>
              </div>
            )}
            <div className="fixed left-0 right-0 bottom-0 md:hidden p-3 bg-white border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)] flex justify-between gap-2">
              <button onClick={()=> setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))} className="px-3 py-2 border rounded-md">Prev</button>
              <button onClick={saveAndNext} className="px-3 py-2 bg-blue-600 text-white rounded-md flex-1">Save & Next</button>
              <button onClick={markAndNext} className="px-3 py-2 border rounded-md">Flag</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test Results Screen
  if (testState === 'completed' && testResults) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Test Results</h1>
              <p className="text-gray-600">{testResults.testTitle}</p>
            </div>

            {/* Score Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {testResults.pcmScore}/200
                </div>
                <div className="text-gray-600">PCM Score</div>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {testResults.accuracy}%
                </div>
                <div className="text-gray-600">Accuracy</div>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {testResults.timeTaken} min
                </div>
                <div className="text-gray-600">Time Taken</div>
              </div>
              <div className="text-center p-6 bg-amber-50 rounded-lg">
                <div className="text-3xl font-bold text-amber-600 mb-2">
                  {testResults.flaggedQuestions}
                </div>
                <div className="text-gray-600">Marked</div>
              </div>
            </div>

            {/* Subject cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {['Physics','Chemistry','Mathematics'].map(s => {
                const st = testResults.subjectBreakdown[s];
                const avg = st.answered ? Math.round(st.timeMs / st.answered / 1000) : 0;
                return (
                  <div key={s} className="p-4 rounded-lg border">
                    <div className="font-semibold mb-1">{s}</div>
                    <div className="text-sm text-gray-700">Score {st.score}/{st.max} • Accuracy {st.answered? Math.round(st.correct/st.answered*100):0}% • Avg time {avg}s</div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button className="flex-1 border py-3 px-6 rounded-lg">Re-attempt incorrect (only)</button>
              <button className="flex-1 border py-3 px-6 rounded-lg">Fix top weak areas (10Q drill)</button>
              <button onClick={()=> setAddWrongToSrs(v=>!v)} className={`flex-1 py-3 px-6 rounded-lg ${addWrongToSrs ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700 border'}`}>
                {addWrongToSrs ? 'Added wrong to Reviews (SRS)' : 'Add wrong to Reviews (SRS)'}
              </button>
            </div>
            {srsQueued && (
              <div className="mt-2 text-xs text-green-700">Queued to SRS.</div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <button
                onClick={() => {
                  setTestState('setup');
                  setTestResults(null);
                }}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700"
              >
                Take Another Test
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MockTestSystem;
